import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  FileValue,
  ImageSchemaItem,
  RowWithEmbeddedMetadata,
  Value,
} from "@data-root/types";
import { SchemaItemProps, useField } from "@data-root/react";
import { inputGuard } from "../../hocs/guard";
import { View } from "@data-root/react";
import { Button, Skeleton, Space, Typography } from "antd";
import OurImagePicker, { ImagePickerConf } from "../picker";
import ParentSize from "../ParentSize";
import { UploadOutlined } from "@ant-design/icons";
import { Remarks } from "../remarks";

const { Title } = Typography;

const config: ImagePickerConf = {
  objectFit: "contain",
  hideDeleteBtn: false,
  hideDownloadBtn: false,
  hideAddBtn: false,
  compressInitial: 50,
};

export const ImageInput: FC<SchemaItemProps<ImageSchemaItem>> = inputGuard(
  ({ item }) => {
    const {
      loaded,
      show,
      editable,
      displayName,
      defaultValue,
      setValue: _setValue,
      remarks,
      subscribe,
    } = useField(item);

    const [value, setValue] = useState<FileValue | null>(
      defaultValue as unknown as FileValue
    );

    const displayValue = useMemo(() => {
      if (!value) return "";

      return value.url;
    }, [value]);

    const handleSetValue = useCallback(
      (url: string | null) => {
        if (url === null) {
          setValue(null);
          _setValue(null, "");
          return;
        }

        const file: FileValue = {
          type: "File",
          url,
        };

        setValue(file);
        _setValue(file as any, url);
      },
      [_setValue]
    );

    const onImageChanged = useCallback(
      async (uri: string | null) => {
        console.log("uri", uri);

        if (!uri) {
          handleSetValue(null);
          return;
        }

        handleSetValue(uri);
      },
      [handleSetValue]
    );

    useEffect(() => {
      const callback = (
        next: RowWithEmbeddedMetadata,
        options?: {
          init?: boolean;
          evaluation?: boolean;
        }
      ) => {
        if (options?.init) {
          setValue(next[item.id] as unknown as FileValue);
          return;
        }
        if (!options?.evaluation) return;

        setValue(next[item.id] as unknown as FileValue);
      };

      const unsubscribe = subscribe(callback);

      return () => {
        unsubscribe();
      };
    }, [item.id, subscribe]);

    if (!show) return null;

    return (
      <View style={style}>
        <Title level={5}>{displayName}</Title>

        {loaded ? (
          <OurImagePicker
            imageSrc={displayValue}
            imageChanged={onImageChanged}
            config={config}
            editable={editable}
          />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={divStyle}>
              <ParentSize>
                {({ width }) => (
                  <Skeleton.Image
                    active
                    style={{
                      width: width,
                      height: 200,
                    }}
                  />
                )}
              </ParentSize>
            </div>

            {editable && (
              <Button disabled icon={<UploadOutlined />}>
                Subir imagen
              </Button>
            )}
          </Space>
        )}

        <Remarks texts={remarks} />
      </View>
    );
  }
);

ImageInput.displayName = "ImageInput";

const style = { width: "100%" };
const divStyle = {
  width: "100%",
  height: 200,
};
